import { useCallback } from 'react';
import toggleStringInArray from '@xmanscript/utils/toggleStringInArray';
import Chip from '../_lib/Chip';

interface Option {
  label: string;
  value?: string;
  _id?: string;
  id?: string;
  [key: string]: any; // Allow indexing with a string for custom keys
}

interface ChipSelectorProps {
  options: Option[];
  // eslint-disable-next-line no-unused-vars
  onChange: (selected: string[]) => void;
  maxCount?: number | null;
  choose?: string;
  value: string[];
  // Add form registration props
  name?: string;
  onBlur?: () => void;
  id?: string;
  enable?: boolean;
  error?: string;
}

export default function ChipSelector({
  options,
  onChange,
  maxCount = null,
  choose = 'value',
  value = [],
  name,
  onBlur,
  id,
  enable = true,
  error,
  ...props
}: ChipSelectorProps) {
  // Ensure value is always an array
  const normalizedValue = Array.isArray(value) ? value : value ? [value] : [];
  
  const handleChipClick = useCallback((optionValue: string) => {
    // Don't allow changes if disabled
    if (!enable) return;
    
    let newValues: string[];
    
    if (maxCount === 1) {
      // For single selection, replace the value
      newValues = normalizedValue.includes(optionValue) ? [] : [optionValue];
    } else {
      // For multiple selection, toggle the value
      newValues = [...toggleStringInArray([...normalizedValue], optionValue)];
    }
    
    // Notify parent/form
    if (onChange) onChange(newValues);
  }, [normalizedValue, maxCount, onChange, enable]);

  // Function to get the identifier value from an option based on the 'choose' parameter
  const getOptionValue = useCallback((option: Option): string => {
    // Try to get the value based on the 'choose' parameter
    if (choose && option.hasOwnProperty(choose)) {
      return String(option[choose as keyof Option]);
    }
    // Fallback to common identifier fields
    if (option._id) return option._id;
    if (option.id) return option.id;
    if (option.value) return option.value;
    // Last resort: use label
    return option.label;
  }, [choose]);

  return (
    <div className="w-full">
      <div 
        id={id}
        className={`flex flex-wrap items-center gap-3 rounded-lg p-4 ${
          error ? 'border-2 border-red-500' : ''
        }`}
        {...props}
      >
        {options?.map((option) => {
          const optionValue = getOptionValue(option);
          return (
            <Chip
              key={`${optionValue}-${option.label}`}
              label={option.label}
              selected={normalizedValue.includes(optionValue)}
              onClick={() => handleChipClick(optionValue)}
              disabled={!enable}
            />
          );
        })}
      </div>
      {error && (
        <p className="mt-2 text-sm text-red-500">{error}</p>
      )}
    </div>
  );
}